import { useCallback, useRef, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import {
  AddressSuggestion,
  ValidateAddressRequest,
} from 'src/models/addressValidation.models'
import { getRequestConfig } from 'src/queries/requestConfig'
import { validateAddress } from 'src/requests/validateAddress.request'

interface UseAddressValidationReturn {
  validateBeforeSubmit: (
    address: ValidateAddressRequest,
    onValid: (suggestion: AddressSuggestion | null) => void,
    onCancel?: () => void,
  ) => Promise<void>
  showSuggestionModal: boolean
  suggestions: AddressSuggestion[]
  isConfirmation: boolean
  onSuggestionSelect: (suggestion: AddressSuggestion) => void
  onSuggestionClose: () => void
  validationError: string | undefined
  isValidating: boolean
}

export const useAddressValidation = (): UseAddressValidationReturn => {
  const translate = useTranslate()

  const [showSuggestionModal, setShowSuggestionModal] = useState(false)
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [isConfirmation, setIsConfirmation] = useState(false)
  const [validationError, setValidationError] = useState<string | undefined>()
  const [isValidating, setIsValidating] = useState(false)
  const pendingCallbackRef = useRef<
    ((suggestion: AddressSuggestion | null) => void) | null
  >(null)
  const pendingCancelCallbackRef = useRef<(() => void) | null>(null)

  const errorCodeToMessage = useCallback(
    (errorCode: string | undefined): string => {
      switch (errorCode) {
        case 'ADDRESS_DOES_NOT_EXIST':
          return translate(
            'The address could not be found. Please check and try again.',
          )
        case 'MISSING_SUB_PREMISE':
          return translate(
            'The address is incomplete. Please include an apartment or unit number.',
          )
        case 'PO_BOX_NOT_ALLOWED':
          return translate('P.O. Box addresses are not allowed.')
        case 'CMRA_NOT_ALLOWED':
          return translate(
            'Commercial mail receiving addresses are not allowed.',
          )
        case 'INVALID_REGION_CODE':
          return translate('The state or province is invalid.')
        default:
          return translate(
            'We could not validate this address. Please check and try again.',
          )
      }
    },
    [translate],
  )

  const validateBeforeSubmit = useCallback(
    async (
      address: ValidateAddressRequest,
      onValid: (suggestion: AddressSuggestion | null) => void,
      onCancel?: () => void,
    ) => {
      setValidationError(undefined)
      setIsValidating(true)

      try {
        const result = await validateAddress(address, getRequestConfig())

        switch (result.status) {
          case 'VALID':
            if (result.suggestions && result.suggestions.length > 0) {
              setSuggestions(result.suggestions)
              setIsConfirmation(true)
              pendingCallbackRef.current = onValid
              pendingCancelCallbackRef.current = onCancel ?? null
              setShowSuggestionModal(true)
            } else {
              onValid(null)
            }
            break

          case 'SUGGESTIONS_AVAILABLE':
            if (result.suggestions && result.suggestions.length > 0) {
              setSuggestions(result.suggestions)
              setIsConfirmation(false)
              pendingCallbackRef.current = onValid
              pendingCancelCallbackRef.current = onCancel ?? null
              setShowSuggestionModal(true)
            } else {
              onValid(null)
            }
            break

          case 'INVALID':
            setValidationError(errorCodeToMessage(result.errorCode))
            if (onCancel) {
              onCancel()
            }
            break
        }
      } catch {
        // Validation endpoint unavailable -- let the form submit normally.
        // Existing server-side validation still protects the downstream endpoint.
        onValid(null)
      } finally {
        setIsValidating(false)
      }
    },
    [errorCodeToMessage],
  )

  const onSuggestionSelect = useCallback(
    (suggestion: AddressSuggestion) => {
      setShowSuggestionModal(false)
      if (pendingCallbackRef.current) {
        pendingCallbackRef.current(suggestion)
        pendingCallbackRef.current = null
      }
      pendingCancelCallbackRef.current = null
    },
    [],
  )

  const onSuggestionClose = useCallback(() => {
    setShowSuggestionModal(false)
    if (pendingCancelCallbackRef.current) {
      pendingCancelCallbackRef.current()
    }
    pendingCallbackRef.current = null
    pendingCancelCallbackRef.current = null
  }, [])

  return {
    validateBeforeSubmit,
    showSuggestionModal,
    suggestions,
    isConfirmation,
    onSuggestionSelect,
    onSuggestionClose,
    validationError,
    isValidating,
  }
}
